import React, { useEffect, useMemo, useState } from 'react';
import { AppShell } from '../../lib/AppShell';
import { Sleeve } from '../../lib/Sleeve';
import { SortTable, ColumnData, Direction } from '../../lib/SortTable';
import { useNavigate } from '@reach/router';
import { CrispoPaginator, PageResults } from '../../lib/CrispoPaginator';
import { CrispoButton } from '../../lib/CrispoButton';
import { useIsMounted } from '../../../hooks/useIsMounted';
import { CrispoSearch } from '../../lib/CrispoSearch';
import RedeemedService, { IRedemption } from './RedeemedService';

export const ReedeemedScreen = () => {
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const [search, setSearch] = useState('');
  const [redemptions, setRedemptions] = useState<IRedemption[]>([]);
  const [sortColumn, setSortColumn] = useState('Date');
  const [sort, setSort] = useState('date');
  const [direction, setDirection] = useState<Direction>(-1);
  const [totalRedemptions, setTotalRedemptions] = useState(RedeemedService.state.query.total);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const totalPages = useMemo(() => Math.ceil(totalRedemptions / limit), [totalRedemptions, limit]);

  useEffect(() => {
    RedeemedService.query(search, sort, direction, page, limit).then((res) => {
      setRedemptions(res.redemptions);
      setTotalRedemptions(res.total);
    });
  }, [search, sort, direction, page, limit, isMounted]);

  useEffect(() => {
    setPage(1);
  }, [search, limit, sort, direction]);

  const RewardSentCheckBox = (props: { item: any }) => (
    <input
      className=''
      type='checkbox'
      defaultChecked={props.item.sent}
      onClick={(e) => e.stopPropagation()}
      onChange={async (change) => {
        RedeemedService.updateSent(props.item.id!, change.currentTarget.checked);
      }}
    />
  );

  const tableData = useMemo(
    () =>
      redemptions.map((r) => {
        let product = '';

        if (r.order.products.length > 1 || r.order.selections.length > 1 || (r.order.products.length > 0 && r.order.selections.length > 0)) {
          product = '(Multiple Products)'
        } else if (r.order.products.length == 1 || r.order.selections.length == 1) {
          product = r.order.products[0]?.product?.title || r.order.selections[0]?.product?.product.title
        }

        return {
          id: r._id!,
          username: r.user.username,
          reward: r.reward,
          product: product,
          type: r.type,
          sent: r.sent,
          date: new Date(r.date).toLocaleDateString(),
        };
      }),
    [redemptions],
  );

  const [exporting, setExporting] = useState(false);

  function escapeCsvField(value: string) {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return '"' + value.replace(/"/g, '""') + '"';
    }
    return value;
  }

  function downloadUnsentCsv() {
    setExporting(true);
    RedeemedService.listUnsent()
      .then((data) => {
        const headers = ['Date', 'Reward', 'Username', 'First Name', 'Last Name', 'Date of Birth', 'Products', 'Selections'];
        const rows = data.map((r) => [
          new Date(r.date).toLocaleDateString(),
          r.reward?.title || '',
          r.user?.username || '',
          r.user?.firstName || '',
          r.user?.lastName || '',
          r.user?.dateOfBirth ? new Date(r.user.dateOfBirth).toLocaleDateString() : '',
          (r.order?.products || []).map((p) => `${p.product.title}${p.option !== "" && p.option !== "None" ? " (" + p.option + ")" : ""}`).join('; '),
          (r.order?.selections || []).map((s) => `${s.product.product.title}${s.product.option !== "" && s.product.option !== "None" ? " (" + s.product.option + ")" : ""}`).join('; '),
        ]);
        const csv = [headers, ...rows].map((row) => row.map(escapeCsvField).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'unsent-rewards.csv';
        link.click();
        URL.revokeObjectURL(url);
      })
      .finally(() => setExporting(false));
  }

  function headerClickHandler(col: ColumnData, nextDirection: Direction) {
    setDirection(nextDirection);
    setSortColumn(col.title);
    setSort(col.property as string || '');
  }

  return (
    <AppShell>
      <Sleeve title='Redeemed'>
        <div className='flex justify-between mb-3'>
          <CrispoSearch
            placeholder='Search by email...'
            onChange={(e) => setSearch(e.target.value)}
          />
          <CrispoButton
            secondary
            title='Download Unsent'
            isLoading={exporting}
            onClick={downloadUnsentCsv}
          />
        </div>
        <div className='flex items-baseline justify-between mb-3'>
          <PageResults
            className='flex items-center justify-end flex-1 text-sm'
            options={[10, 25, 50, 100]}
            activeOption={limit}
            onOptionClick={setLimit}
          />
        </div>
        <div className='mb-10'>
          <SortTable
            sortColumn={sortColumn}
            direction={direction}
            onHeaderClick={headerClickHandler}
            onItemClick={(obj) => {
              navigate(`/purchases/${obj.id}`);
            }}
            forceDirection
            columns={[
              {
                title: 'Username',
                property: 'username',
              },
              {
                title: 'Reward',
                property: (i) => i.reward.title,
              },
              {
                title: 'Product',
                property: 'product',
              },
              { title: 'Date', property: 'date' },
              {
                title: 'Reward Sent',
                property: 'sent',
                component: RewardSentCheckBox,
              },
            ]}
            data={tableData}
          />
          <div className='items-center justify-center mt-5 mb-5 xl:flex'>
            <span className='flex-1 text-xs text-left'>
              Showing {limit * page - limit + 1}-{Math.min(limit * page, totalRedemptions)} out of {totalRedemptions} results
            </span>
            {totalPages > 1 && <CrispoPaginator pageCount={totalPages} currentPage={page} onPageChange={setPage} />}
            <span className='flex-1' />
          </div>
        </div>
      </Sleeve>
    </AppShell>
  );
};
